/**
 * TMCOMAS remanufacturing activity catalog (from partner Excel).
 * Sellable marketplace listings use category titles only; individual activities
 * are selected when the provider builds order milestones after quote acceptance.
 *
 * @see product-additional-attributes-remanufacturing-extension.json
 */

export const REMANUFACTURING_ACTIVITY_CATALOG_VERSION = "1.1.0";

/** @typedef {"done_tick"|"percent_completion"} RemanufacturingProgressType */
/** @typedef {"certificate"|"pictures_and_videos"|"none"} RemanufacturingOutcomeType */

/**
 * @typedef {Object} RemanufacturingActivityCategory
 * @property {string} id Stable category id (used in schema validation).
 * @property {string} excelSectionTitle Section header label in the partner Excel.
 * @property {string} listingTitle Business title for sellable marketplace listings.
 * @property {boolean} sellable Whether providers may publish this category as a standard listing.
 * @property {number} sortOrder Display order in milestone builder UI.
 */

/**
 * @typedef {Object} RemanufacturingActivity
 * @property {string} id Stable activity id (slug).
 * @property {string} categoryId Parent category id.
 * @property {string} label Human-readable activity name.
 * @property {RemanufacturingProgressType} progressType
 * @property {RemanufacturingOutcomeType} outcomeType
 * @property {string[]} [acceptedFileTypes] Lowercase extensions without dots; empty when outcomeType is none.
 */

export const REMANUFACTURING_PROGRESS_TYPE = {
  DONE_TICK: "done_tick",
  PERCENT_COMPLETION: "percent_completion",
};

export const REMANUFACTURING_OUTCOME_TYPE = {
  CERTIFICATE: "certificate",
  PICTURES_AND_VIDEOS: "pictures_and_videos",
  NONE: "none",
};

/** @type {RemanufacturingActivityCategory[]} */
export const REMANUFACTURING_ACTIVITY_CATEGORIES = [
  {
    id: "reception_identification",
    excelSectionTitle: "Reception & Identification",
    listingTitle: "Reception & Identification",
    sellable: false,
    sortOrder: 1,
  },
  {
    id: "inspection",
    excelSectionTitle: "Inspection activities",
    listingTitle: "Inspection Services",
    sellable: true,
    sortOrder: 2,
  },
  {
    id: "machining",
    excelSectionTitle: "Machining activities",
    listingTitle: "Machining Services",
    sellable: true,
    sortOrder: 3,
  },
  {
    id: "repair",
    excelSectionTitle: "Repair technologies",
    listingTitle: "Repair Technologies",
    sellable: true,
    sortOrder: 4,
  },
  {
    id: "repair_component_replacement",
    excelSectionTitle: "Component Replacement",
    listingTitle: "Component Replacement",
    sellable: false,
    sortOrder: 5,
  },
  {
    id: "reassembly",
    excelSectionTitle: "Reassembly",
    listingTitle: "Reassembly",
    sellable: false,
    sortOrder: 6,
  },
  {
    id: "quality_control",
    excelSectionTitle: "Quality control techniques",
    listingTitle: "Quality Control Services",
    sellable: true,
    sortOrder: 7,
  },
  {
    id: "miscellaneous",
    excelSectionTitle: "miscellania",
    listingTitle: "Miscellania",
    sellable: false,
    sortOrder: 8,
  },
  {
    id: "certificates_documentation",
    excelSectionTitle: "Certificates/documentation",
    listingTitle: "Certificates / Documentation",
    sellable: false,
    sortOrder: 9,
  },
  {
    id: "logistics_readiness",
    excelSectionTitle: "Logistics & Readiness",
    listingTitle: "Logistics & Readiness",
    sellable: false,
    sortOrder: 10,
  },
];

/** @type {readonly string[]} */
export const SELLABLE_REMANUFACTURING_SERVICE_CATEGORY_IDS =
  REMANUFACTURING_ACTIVITY_CATEGORIES.filter((category) => category.sellable).map(
    (category) => category.id
  );

/**
 * @param {string} label
 */
function slugifyActivityLabel(label) {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * @param {string} categoryId
 * @param {string} label
 * @param {RemanufacturingProgressType} progressType
 * @param {RemanufacturingOutcomeType} outcomeType
 * @param {string[]} [acceptedFileTypes]
 * @returns {RemanufacturingActivity}
 */
function activity(
  categoryId,
  label,
  progressType,
  outcomeType,
  acceptedFileTypes = []
) {
  return {
    id: slugifyActivityLabel(label),
    categoryId,
    label,
    progressType,
    outcomeType,
    ...(acceptedFileTypes.length > 0 ? { acceptedFileTypes } : {}),
  };
}

const PDF = ["pdf"];
const MEDIA = ["jpg", "mov", "avi"];

/** @type {RemanufacturingActivity[]} */
export const REMANUFACTURING_ACTIVITIES = [
  // Inspection activities
  activity(
    "inspection",
    "Reception visual inspection",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "Reception photographic inspection",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.PICTURES_AND_VIDEOS,
    MEDIA
  ),
  activity(
    "inspection",
    "hot water & soap cleaning",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "solvent cleaning",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "glass bead cleaning",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "component disassembly",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "inspection",
    "component visual inspection",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "component photographic inspection",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.PICTURES_AND_VIDEOS,
    MEDIA
  ),
  activity(
    "inspection",
    "component hot water & soap cleaning",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "component solvent cleaning",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "component glass bead cleaning",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "component dimensional check",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "component run-out",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "component NDT: visual inspection test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "component NDT: dye penetrant test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "component NDT: ultrasound test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "component NDT: magnetic particle test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "main component visual inspection",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "main component photographic inspection",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.PICTURES_AND_VIDEOS,
    MEDIA
  ),
  activity(
    "inspection",
    "main component hot water & soap cleaning",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "main component solvent cleaning",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "main component glass bead cleaning",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "main component dimensional check",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "main component run-out",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "main component NDT: visual inspection test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "main component NDT: dye penetrant test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "main component NDT: ultrasound test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "main component NDT: magnetic particle test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "inspection",
    "External accessories photographic inspection",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "inspection",
    "Oil draining",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "inspection",
    "Removal of external accessories",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "inspection",
    "Bearing condition inspection",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "inspection",
    "Seal and sealing-surface inspection",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "inspection",
    "Gear tooth contact / wear assessment",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "inspection",
    "Lubricant / oil condition analysis",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "inspection",
    "Repair / replacement photographic documentation",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  // Machining activities
  activity(
    "machining",
    "rough machining",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "machining",
    "milling",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "machining",
    "grinding",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "machining",
    "skim cut",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "machining",
    "polish",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "machining",
    "lapping",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "machining",
    "honing",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "machining",
    "beveling",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "machining",
    "straightening",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "machining",
    "bending",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  // Repair technologies
  activity(
    "repair",
    "TIG welding",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "SMAW welding",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "MIG welding",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "laser cladding",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "HVOF thermal spraying",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "APS thermal spraying",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "AS thermal spraying",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "FS thermal spraying",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "grit blasting",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "compress air blowing",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "masking",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "preheating",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "cooling",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "painting",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "Gear repair",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "Shaft repair",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "Housing repair",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair",
    "Bearing-seat repair",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  // Quality control techniques
  activity(
    "quality_control",
    "roughness control",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "quality_control",
    "dimensional control",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "quality_control",
    "electrical run-out",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "quality_control",
    "low speed balancing check",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "quality_control",
    "thickness control",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "quality_control",
    "Final visual inspection",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "quality_control",
    "Leakage test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "quality_control",
    "Basic spin test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "quality_control",
    "Full test run",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "quality_control",
    "Customer-specified test run",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "quality_control",
    "Vibration measurement during test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "quality_control",
    "Temperature monitoring during test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "quality_control",
    "Noise assessment during test",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "quality_control",
    "Final quality release",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  // Miscellania (milestone-only)
  activity(
    "miscellaneous",
    "marking",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "miscellaneous",
    "robot programming",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "miscellaneous",
    "protecting",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "miscellaneous",
    "oiling",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "miscellaneous",
    "packing",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "miscellaneous",
    "3D scan",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "miscellaneous",
    "EOS material analysis",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  // Certificates / documentation (milestone-only)
  activity(
    "certificates_documentation",
    "quality control plan",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "Inspection and test plan (ITP)",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "material certificate",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "heat treatment certificate",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "roughness certificate",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "dimensional certificate",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "thickness certificate",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "WPS/WPQR/welding book",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "electrical run-out certificate",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "run-out certificate",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "perheating certificate",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "painture certificate",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "inspection report",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "repair report",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE,
    PDF
  ),
  activity(
    "certificates_documentation",
    "Replaced parts list",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "certificates_documentation",
    "Test documentation package",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "certificates_documentation",
    "As-refurbished photographic documentation",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "certificates_documentation",
    "Warranty issuance",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "certificates_documentation",
    "Traceability documentation",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  // Reception & Identification (milestone-only)
  activity(
    "reception_identification",
    "Reception and registration",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "reception_identification",
    "Identification / nameplate verification",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "reception_identification",
    "Compatibility verification",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "reception_identification",
    "Type confirmation",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "reception_identification",
    "Scope of delivery verification",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "reception_identification",
    "Service scope / quotation confirmation",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  // Component Replacement (milestone-only)
  activity(
    "repair_component_replacement",
    "Bearing replacement",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair_component_replacement",
    "Seal replacement",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair_component_replacement",
    "Gear replacement",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair_component_replacement",
    "Shaft replacement",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair_component_replacement",
    "Replacement of fasteners and service consumables",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "repair_component_replacement",
    "Reserved spare-part allocation",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  // Reassembly (milestone-only)
  activity(
    "reassembly",
    "Reassembly",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "reassembly",
    "Bearing installation and setting",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "reassembly",
    "Gear alignment / contact adjustment",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "reassembly",
    "Seal installation",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "reassembly",
    "Lubrication system preparation",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "reassembly",
    "Accessory reinstallation",
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "reassembly",
    "Lubricant filling",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  // Logistics & Readiness (milestone-only)
  activity(
    "logistics_readiness",
    "Scope-of-delivery final verification",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "logistics_readiness",
    "Estimated readiness update",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "logistics_readiness",
    "Ready for dispatch",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
  activity(
    "logistics_readiness",
    "Shipment / collection handover",
    REMANUFACTURING_PROGRESS_TYPE.DONE_TICK,
    REMANUFACTURING_OUTCOME_TYPE.NONE
  ),
];

const activityById = new Map(
  REMANUFACTURING_ACTIVITIES.map((row) => [row.id, row])
);

const categoryById = new Map(
  REMANUFACTURING_ACTIVITY_CATEGORIES.map((row) => [row.id, row])
);

/**
 * @param {string} categoryId
 */
export function getRemanufacturingActivityCategory(categoryId) {
  return categoryById.get(categoryId) ?? null;
}

/**
 * @param {string} activityId
 */
export function getRemanufacturingActivity(activityId) {
  return activityById.get(activityId) ?? null;
}

/**
 * @returns {RemanufacturingActivityCategory[]}
 */
export function listSellableRemanufacturingServiceCategories() {
  return REMANUFACTURING_ACTIVITY_CATEGORIES.filter(
    (category) => category.sellable
  );
}

/**
 * @param {string} categoryId
 */
export function isSellableRemanufacturingServiceCategory(categoryId) {
  const category = getRemanufacturingActivityCategory(categoryId);
  return Boolean(category?.sellable);
}

/**
 * @param {string} [categoryId] When omitted, returns the full catalog.
 * @returns {RemanufacturingActivity[]}
 */
export function listRemanufacturingActivities(categoryId) {
  if (!categoryId) {
    return [...REMANUFACTURING_ACTIVITIES];
  }
  return REMANUFACTURING_ACTIVITIES.filter(
    (row) => row.categoryId === categoryId
  );
}

/**
 * Validation pattern for Back Office `remanufacturingServiceCategory` field.
 * @type {string}
 */
export const REMANUFACTURING_SERVICE_CATEGORY_VALIDATION_RULE = `^(${SELLABLE_REMANUFACTURING_SERVICE_CATEGORY_IDS.join("|")})(,(${SELLABLE_REMANUFACTURING_SERVICE_CATEGORY_IDS.join("|")}))*$`;
